import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import type { GetTeamDetailResponse, TeamListItemResponse } from "@/api/modules/teamManage";
import { useUserStore } from "./user";
import { TeamManageApi } from "@/api/modules/teamManage";
import { useMessage } from "@/composables/useMessage";

/**
 * 团队状态管理
 * @description 管理当前登录账号的团队列表、当前所选团队及团队权限状态
 */
export const useTeamStore = defineStore("team", () => {
  const userStore = useUserStore();
  const message = useMessage();

  /**
   * 已加入的团队列表
   */
  const joinedTeams = ref<TeamListItemResponse[]>([]);

  /**
   * 当前选中的团队
   */
  const currentTeam = ref<GetTeamDetailResponse | null>(null);

  /**
   * 团队初始化加载状态
   */
  const teamLoading = ref(false);

  /**
   * 标记当前账号是否有权限访问团队管理
   * 1. 团队账号 下的管理员 有权限访问团队管理功能，展示团队管理和积分管理
   * 2. 个人账号 也可以展示团队
   * @default false
   */
  const hasTeamPermission = computed<boolean>(() => {
    if (isTeamAccount.value) {
      return !!(currentTeam.value?.team_id && currentTeam.value.role === 1);
    } else {
      return true;
    }
  });

  /**
   * 标记当前是否所处在某个团队下
   * 通过是否有团队ID 和 状态是否为生效中 判断是否正处在某个团队下
   */
  const isTeamAccount = computed<boolean>(() => {
    return !!currentTeam.value?.team_id;
  });

  /**
   * 标记当前团队是否为生效中
   * 通过是否有团队ID 和 状态是否为生效中 判断是否正处在某个团队下
   * @default false
   */
  const TeamIsActive = computed<boolean>(() => {
    return isTeamAccount.value && currentTeam.value?.status === 1;
  });

  /**
   * 获取用户已加入的团队列表
   * 从后端获取当前用户已加入的所有团队信息
   */
  async function fetchJoinedTeams() {
    if (!userStore.isLogin) {
      return;
    }
    console.log("获取用户已加入的团队列表");
    try {
      const response = await TeamManageApi.getUserTeamList({
        page: 1,
        page_size: 100,
      });
      if (response.code === 1) {
        joinedTeams.value = response.data?.data || [];
      } else {
        message.error(response.msg || "获取团队列表失败");
      }
    } catch (error) {
      console.error("[TeamStore] 获取团队列表失败:", error);
      joinedTeams.value = [];
    }
  }

  /**
   * 切换团队
   */
  async function switchTeam(teamId: string) {
    try {
      const response = await TeamManageApi.switchTeam({ team_id: teamId });
      if (response.code === 1) {
        message.success(`已切换团队`);
        // 设置用户信息
        userStore.setUserInfo({
          ...userStore.userInfo!,
          last_active_team_id: response.data?.last_active_team_id || "",
        });

        window.location.href = "/app/dashboard";
      } else {
        message.error(response.msg || "切换团队失败");
      }
    } catch (error) {
      console.error("[TeamStore] 切换团队失败:", error);
    }
  }

  /**
   * 根据 userInfo.last_active_team_id 初始化当前团队
   *
   * 业务规则：
   * - last_active_team_id 为 null/undefined/空字符串：个人账号模式，currentTeam 置为 null
   * - last_active_team_id 有合法值：调用 API 获取团队详情并缓存到 currentTeam
   * - API 调用失败（网络异常/无效 ID）：currentTeam 置为 null，不影响用户正常使用
   */
  async function initCurrentTeam() {
    const lastActiveTeamId = userStore.userInfo?.last_active_team_id;

    if (!lastActiveTeamId) {
      currentTeam.value = null;
      return;
    }

    // const loading = ElLoading.service({
    //   lock: true,
    //   text: "团队信息加载中...",
    //   background: "rgba(0, 0, 0, 0.7)",
    // });
    teamLoading.value = true;
    try {
      const response = await TeamManageApi.getTeamDetail({ team_id: lastActiveTeamId as string });
      if (response.code === 1 && response.data) {
        currentTeam.value = response.data as GetTeamDetailResponse;
      } else {
        currentTeam.value = null;
        console.warn("[TeamStore] 获取团队详情失败:", response.msg || "未知错误");
      }
    } catch (error) {
      console.error("[TeamStore] 获取团队详情异常:", error);
    } finally {
      teamLoading.value = false;
      // loading.close();
    }
  }

  /**
   * 手动设置当前团队
   * @param team - 团队详情信息，传入 null 表示切换到个人账号模式
   */
  function setCurrentTeam(team: GetTeamDetailResponse | null) {
    currentTeam.value = team;
  }

  /**
   * 清除团队状态（退出登录时调用）
   * 重置所有团队相关状态到初始值
   */
  function clearTeamState() {
    joinedTeams.value = [];
    currentTeam.value = null;
    teamLoading.value = false;
  }

  /**
   * 团队成员列表刷新版本号
   * 每次调用 refreshTeamMembers 时递增，组件可 watch 此值触发数据重新获取
   */
  const memberListRefreshKey = ref(0);

  /**
   * 刷新当前团队成员列表
   * 递增版本号，通知组件重新获取成员列表数据
   */
  function refreshTeamMembers() {
    memberListRefreshKey.value++;
  }

  /**
   * 监听用户信息变化，自动初始化团队状态
   * 当 userInfo 更新（登录/页面刷新/跨标签页同步）时，自动触发团队初始化
   */
  watch(
    [
      () => userStore.userInfo?.id,
      () => userStore.userInfo?.last_active_team_id,
    ],
    (values) => {
      if (values[0]) {
        initCurrentTeam();
        fetchJoinedTeams();
      } else {
        clearTeamState();
      }
    },
    { immediate: true }
  );

  return {
    joinedTeams,
    currentTeam,
    teamLoading,
    hasTeamPermission,
    isTeamAccount,
    fetchJoinedTeams,
    initCurrentTeam,
    setCurrentTeam,
    clearTeamState,
    memberListRefreshKey,
    refreshTeamMembers,
    switchTeam,
    TeamIsActive,
  };
});
